import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  type OnChanges,
  type OnInit,
  type SimpleChanges,
} from '@angular/core';
import {
  ServiceOfferingShopCategoryServicePriceType as ServicePriceType,
  type ServiceOfferingShopCategoryService,
  type ServiceOfferingWithOwnerDetails,
} from '@freelancer/datastore/collections';
import {
  HoverColor,
  IconBackdrop,
  IconBackdropStyle,
  IconColor,
  IconSize,
} from '@freelancer/ui/icon';
import { LinkUnderline } from '@freelancer/ui/link';
import { PictureLayout, PictureObjectFit } from '@freelancer/ui/picture';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { UsernameSize, type UsernameBadges } from '@freelancer/ui/username';
import { CorporateStatusApi } from 'api-typings/users/users';
import { CategoryServiceActionOptions } from './service-offering-carousel.types';

@Component({
  selector: 'fl-service-offering-carousel-card',
  template: `
    <div *ngIf="isLoading">
      <fl-loading-text
        class="LoadingCard"
        [rows]="1"
        [padded]="false"
      >
      </fl-loading-text>
    </div>
    <div
      *ngIf="service && !isLoading"
      class="ServiceCard"
    >
      <div
        *ngIf="isEditMode && isShopCategoryService"
        class="Actions"
      >
        <div>
          <fl-icon
            *ngIf="serviceActionOptions.edit"
            i18n-label="Edit service button text"
            [name]="'ui-edit'"
            [clickable]="true"
            [clickAnimation]="'shrink'"
            [color]="IconColor.LIGHT"
            [hoverAnimation]="'none'"
            [flMarginRight]="'xxxsmall'"
            [label]="'Edit service'"
            [size]="IconSize.SMALL"
            [useIconFont]="true"
            (click)="triggerEditService($event, service)"
          ></fl-icon>
        </div>
        <div class="Actions-right">
          <fl-icon
            *ngIf="serviceActionOptions.remove"
            i18n-label="Remove service button text"
            [name]="'ui-subtract-circle'"
            [clickable]="true"
            [clickAnimation]="'shrink'"
            [color]="IconColor.LIGHT"
            [hoverAnimation]="'none'"
            [flMarginRight]="'xxxsmall'"
            [label]="'Remove service'"
            [size]="IconSize.SMALL"
            [useIconFont]="true"
            (click)="triggerRemoveService($event, service)"
          ></fl-icon>
          <fl-icon
            *ngIf="serviceActionOptions.toggleVisibility"
            [name]="'ui-visibility'"
            [clickable]="true"
            [clickAnimation]="'shrink'"
            [color]="IconColor.LIGHT"
            [hoverAnimation]="'none'"
            [size]="IconSize.SMALL"
            [useIconFont]="true"
            (click)="triggerToggleVisibility($event, service)"
          ></fl-icon>
          <fl-icon
            *ngIf="serviceActionOptions.drag"
            [name]="'ui-drag-indicator'"
            [clickable]="true"
            [clickAnimation]="'shrink'"
            [color]="IconColor.LIGHT"
            [hoverAnimation]="'none'"
            [flMarginRight]="'xxxsmall'"
            [size]="IconSize.SMALL"
            [useIconFont]="true"
          ></fl-icon>
        </div>
      </div>
      <fl-link
        flTrackingLabel="ViewService"
        [link]="serviceUrl"
        [queryParams]="fromUrl ? { from: fromUrl } : {}"
        [underline]="LinkUnderline.NEVER"
      >
        <div class="ImageContainer">
          <fl-picture
            class="ServiceCard-media"
            [class.IsEditable]="isEditMode"
            alt="Shopfront service card media"
            i18n-alt="Shopfront service card media"
            [alignCenter]="true"
            [borderRadiusBottom]="true"
            [borderRadiusTop]="true"
            [fixedAspectRatio]="true"
            [flMarginBottom]="'xxsmall'"
            [layout]="PictureLayout.RESPONSIVE"
            [src]="imageUrl"
            [sizes]="imageSizes"
            [width]="442"
            (imageLoaded)="isImageReady = true"
          >
          </fl-picture>
          <fl-loading-text
            *ngIf="!isImageReady"
            class="ImageLoading"
            [padded]="false"
          ></fl-loading-text>
        </div>
        <fl-text
          i18n="Service offering title"
          [maxHeight]="'max-content'"
          [maxLines]="1"
          [size]="titleSize"
          [sizeDesktop]="titleSizeDesktop"
        >
          {{ service.title }}
        </fl-text>
      </fl-link>
      <div class="ServiceCard-details">
        <fl-username
          *ngIf="username"
          class="ServiceCard-username"
          [isBold]="false"
          [link]="'/u/' + username"
          [username]="username"
          [badges]="userBadges"
          [size]="usernameSize"
          [sizeTablet]="usernameSizeTablet"
          [sizeDesktop]="usernameSizeDesktop"
          [truncateText]="true"
        >
        </fl-username>
        <ng-container *ngIf="service?.priceType as type">
          <fl-text
            *ngIf="
              type === ServicePriceType.SUBSCRIPTION_ONLY_MONTHLY ||
              type === ServicePriceType.SUBSCRIPTION_MONTHLY_AND_HOURLY
            "
            i18n="Service offering price for monthly subscription"
            [size]="priceSize"
            [sizeDesktop]="priceSizeDesktop"
            [weight]="FontWeight.MEDIUM"
          >
            {{
              service.price
                | flCurrency
                  : 'USD'
                  : { withPrecision: false, disableCode: true }
            }}
            / month
          </fl-text>
          <fl-text
            *ngIf="
              type === ServicePriceType.SUBSCRIPTION_ONLY_ANNUALLY ||
              type === ServicePriceType.SUBSCRIPTION_ANNUALLY_AND_HOURLY
            "
            i18n="Service offering price for yearly subscription"
            [size]="priceSize"
            [sizeDesktop]="priceSizeDesktop"
            [weight]="FontWeight.MEDIUM"
          >
            {{
              service.price
                | flCurrency
                  : 'USD'
                  : { withPrecision: false, disableCode: true }
            }}
            / year
          </fl-text>
          <fl-text
            *ngIf="
              type === ServicePriceType.SUBSCRIPTION_ONLY_WEEKLY ||
              type === ServicePriceType.SUBSCRIPTION_WEEKLY_AND_HOURLY
            "
            i18n="Service offering price for weekly subscription"
            [size]="priceSize"
            [sizeDesktop]="priceSizeDesktop"
            [weight]="FontWeight.MEDIUM"
          >
            {{
              service.price
                | flCurrency
                  : 'USD'
                  : { withPrecision: false, disableCode: true }
            }}
            / week
          </fl-text>
          <div
            *ngIf="type === ServicePriceType.HOURLY_ONLY"
            class="CardPriceContainer"
          >
            <fl-text
              i18n="Service offering price for hourly"
              [flMarginRight]="discountedPrice ? 'xxsmall' : 'xxxsmall'"
              [size]="priceSize"
              [sizeDesktop]="priceSizeDesktop"
              [textDecoration]="discountedPrice ? 'line-through' : undefined"
              [weight]="FontWeight.MEDIUM"
            >
              {{
                service.price
                  | flCurrency
                    : 'USD'
                    : { withPrecision: false, disableCode: true }
              }}
            </fl-text>
            <fl-text
              *ngIf="discountedPrice"
              i18n="Service offering for hourly discounted price"
              [flMarginRight]="'xxxsmall'"
              [size]="priceSize"
              [sizeDesktop]="priceSizeDesktop"
              [weight]="FontWeight.BLACK"
            >
              {{
                discountedPrice
                  | flCurrency
                    : 'USD'
                    : { withPrecision: false, disableCode: true }
              }}
            </fl-text>
            <fl-text
              i18n="Service offering price for hourly"
              [size]="priceSize"
              [sizeDesktop]="priceSizeDesktop"
              [weight]="FontWeight.MEDIUM"
            >
              / hour
            </fl-text>
          </div>
          <div
            *ngIf="type === ServicePriceType.HAS_FIXED_ITEMS"
            [class.CardPriceContainer]="
              type === ServicePriceType.HAS_FIXED_ITEMS && discountedPrice
            "
          >
            <fl-text
              i18n="Service offering price"
              [flMarginRight]="discountedPrice ? 'xxsmall' : undefined"
              [size]="priceSize"
              [sizeDesktop]="priceSizeDesktop"
              [textDecoration]="discountedPrice ? 'line-through' : undefined"
              [weight]="FontWeight.MEDIUM"
            >
              {{
                service.price
                  | flCurrency
                    : 'USD'
                    : { withPrecision: false, disableCode: true }
              }}
            </fl-text>
            <fl-text
              *ngIf="discountedPrice"
              i18n="Service offering discounted price"
              [size]="priceSize"
              [sizeDesktop]="priceSizeDesktop"
              [weight]="FontWeight.BLACK"
            >
              {{
                discountedPrice
                  | flCurrency
                    : 'USD'
                    : { withPrecision: false, disableCode: true }
              }}
            </fl-text>
          </div>
        </ng-container>
      </div>
    </div>
    <div
      *ngIf="isEditMode && !service && !isLoading"
      class="AddService"
      tabindex="0"
      flTrackingLabel="AddService"
      (click)="addService.emit()"
    >
      <fl-icon
        [name]="'ui-add-circle'"
        [flMarginBottom]="'xxsmall'"
        [useIconFont]="true"
      ></fl-icon>
      <p
        class="text-xsmall desktop:text-small"
        i18n="Add a service offering"
      >
        Add a service
      </p>
    </div>
    <div
      *ngIf="showSeeMoreCard && !isEditMode && !isLoading"
      tabindex="0"
      flTrackingLabel="SeeMoreServices"
    >
      <fl-link
        class="SeeMoreServices"
        flTrackingLabel="ViewService"
        [link]="'/freemarket'"
        [underline]="LinkUnderline.NEVER"
      >
        <fl-picture
          alt="See more service card media"
          i18n-alt="See more service card media"
          [alignCenter]="true"
          [borderRadiusBottom]="true"
          [borderRadiusTop]="true"
          [fixedAspectRatio]="true"
          [src]="seeMoreImageSrc"
          [width]="442"
          [flMarginBottom]="'xxxsmall'"
        >
        </fl-picture>
        <p
          class="text-light text-mid SeeMoreServicesText desktop:text-large font-bold"
          i18n="Service offering title"
        >
          See more services
        </p>
      </fl-link>
    </div>
    <div
      *ngIf="emptyCard && !isEditMode && !isLoading"
      class="EmptyCategoryCard"
    >
      <fl-picture
        alt="Empty category coming soon card media"
        i18n-alt="Empty category coming soon card media"
        class="EmptyCategoryPicture"
        [alignCenter]="true"
        [borderRadiusBottom]="true"
        [borderRadiusTop]="true"
        [fixedAspectRatio]="true"
        [flMarginBottom]="'xxsmall'"
        [objectFit]="PictureObjectFit.CONTAIN"
        [src]="emptyCategoryImageSrc"
        [width]="442"
      >
      </fl-picture>
      <p
        class="text-marketing_small desktop:text-mid"
        i18n="Coming soon text"
      >
        Coming Soon
      </p>
    </div>
  `,
  styleUrls: ['./service-offering-carousel-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceOfferingCarouselCardComponent implements OnChanges, OnInit {
  FontColor = FontColor;
  FontWeight = FontWeight;
  HoverColor = HoverColor;
  IconBackdrop = IconBackdrop;
  IconBackdropStyle = IconBackdropStyle;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkUnderline = LinkUnderline;
  PictureLayout = PictureLayout;
  PictureObjectFit = PictureObjectFit;
  TextSize = TextSize;
  ServicePriceType = ServicePriceType;

  @Input() service?:
    | ServiceOfferingShopCategoryService
    | ServiceOfferingWithOwnerDetails;
  @Input() emptyCard = false;
  @Input() isEditMode = false;
  @Input() isLoading = false;
  @Input() imageSizes?: string;
  @Input() serviceActionOptions: CategoryServiceActionOptions;
  @Input() titleSize = TextSize.XSMALL;
  @Input() titleSizeTablet = TextSize.XSMALL;
  @Input() titleSizeDesktop = TextSize.SMALL;
  @Input() priceSize = TextSize.SMALL;
  @Input() priceSizeTablet = TextSize.SMALL;
  @Input() priceSizeDesktop = TextSize.MARKETING_SMALL;
  @Input() usernameSize = UsernameSize.MID;
  @Input() usernameSizeTablet = UsernameSize.MID;
  @Input() usernameSizeDesktop = UsernameSize.LARGE;
  @Input() useSmallerPriceSize = false;
  @Input() fromUrl?: string;
  @Input() hideUsername = false;
  @Input() showSeeMoreCard = false;

  // FIXME: T300116 Enable hydration. Currently fails on client side with
  // TypeError: Cannot read properties of null (reading 'firstChild')
  @HostBinding('attr.ngSkipHydration') ngSkipHydration = true;

  userBadges?: UsernameBadges;
  username?: string;
  imageUrl: string;
  serviceUrl: string;
  isShopCategoryService: boolean;
  discountedPrice?: number;

  isImageReady = false;
  seeMoreImageSrc = 'services/see-more.png';
  emptyCategoryImageSrc = 'shopfronts/empty-category-box.png';

  @Output() addService = new EventEmitter<void>();
  @Output() editService =
    new EventEmitter<ServiceOfferingShopCategoryService>();
  @Output() toggleVisibility =
    new EventEmitter<ServiceOfferingShopCategoryService>();
  @Output() removeService =
    new EventEmitter<ServiceOfferingShopCategoryService>();

  ngOnChanges(changes: SimpleChanges): void {
    if ('service' in changes && this.service !== undefined) {
      if (this.isShopCategoryServiceType(this.service)) {
        this.isShopCategoryService = true;
        this.serviceUrl = `/service/${
          this.service.seoUrl ?? this.service.serviceOfferingId
        }`;
        this.imageUrl = this.service.heroImageUrl ?? '';
        this.username = undefined;
        this.userBadges = undefined;
      }

      if (this.isServiceOfferingWithOwnerDetailsType(this.service)) {
        this.isShopCategoryService = false;
        this.serviceUrl = `/service/${this.service.seoUrl ?? this.service.id}`;
        this.imageUrl = this.service.attachments?.[0]?.downloadable_url ?? '';
        this.username = this.service.owner.username;
        this.userBadges = {
          corporate:
            this.service.owner.corporate?.status === CorporateStatusApi.ACTIVE,
          preferred_freelancer: this.service.owner.preferredFreelancer,
          verified: this.service.owner.status.freelancerVerifiedUser,
        };
      }

      // Check for discount validity and set the discounted price here
      // return undefined if discount validity is over or not set
      this.discountedPrice =
        this.service?.discountDetails &&
        Date.now() <= this.service.discountDetails.validUntil &&
        Date.now() >= this.service.discountDetails.validFrom
          ? this.service.discountDetails.discountedPrice
          : undefined;
    }
  }

  ngOnInit(): void {
    if (this.useSmallerPriceSize) {
      this.priceSize = TextSize.XSMALL;
      this.priceSizeTablet = TextSize.XSMALL;
      this.priceSizeDesktop = TextSize.SMALL;
    }
  }

  isShopCategoryServiceType(
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): service is ServiceOfferingShopCategoryService {
    return 'categoryId' in service;
  }

  isServiceOfferingWithOwnerDetailsType(
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): service is ServiceOfferingWithOwnerDetails {
    return 'status' in service;
  }

  // Stop propagation of icon click events, since the service card itself may become clickable in the future
  triggerEditService(
    event: MouseEvent,
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): void {
    if (this.isShopCategoryServiceType(service)) {
      event.stopPropagation();
      this.editService.emit(service);
    }
  }

  triggerToggleVisibility(
    event: MouseEvent,
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): void {
    if (this.isShopCategoryServiceType(service)) {
      event.stopPropagation();
      this.toggleVisibility.emit(service);
    }
  }

  triggerRemoveService(
    event: MouseEvent,
    service:
      | ServiceOfferingShopCategoryService
      | ServiceOfferingWithOwnerDetails,
  ): void {
    if (this.isShopCategoryServiceType(service)) {
      event.stopPropagation();
      this.removeService.emit(service);
    }
  }
}
